import { APIClient } from "../../helpers/api_helper";

const api = new APIClient();

const AUDIT_LOGS_ENDPOINT = "/api/v1/admin/rbac/rbac-audit-logs";

export interface AuditLogItem {
  _id?: string;
  id?: string;
  timestamp?: string;
  createdAt?: string;
  action?: string;
  actor?: string;
  actorName?: string;
  user?: string;
  actedon?: string;
  actedOn?: string;
  actedOnName?: string;
  target?: string;
  targetName?: string;
  permission?: string;
  permissionCode?: string;
  before?: string;
  previousValue?: string;
  after?: string;
  newValue?: string;
  scope?: string;
  scopeType?: string;
  scopeId?: string;
  scope_id?: string;
  resource?: string;
  resourceType?: string;
  result?: string;
  outcome?: string;
  details?: Record<string, any>;
  targetType?: string;
  [key: string]: any;
}

export interface AuditLogsMeta {
  total: number;
  page: number;
  limit: number;
  pages: number;
}

export interface ListAuditLogsResponse {
  data: AuditLogItem[];
  meta: AuditLogsMeta;
}

export interface ListAuditLogsParams {
  page?: number;
  limit?: number;
  action?: string;
  targetId?: string;
  actor?: string;
  from?: string;
  to?: string;
  // Additional filters can be added here later (user, scopeType, result, etc.)
}

/**
 * List audit logs from the RBAC audit logs endpoint.
 * Currently only uses pagination; filters can be added as needed.
 */
export const listAuditLogs = async (
  params: ListAuditLogsParams
): Promise<ListAuditLogsResponse> => {
  const { page = 1, limit = 10, action, targetId, actor, from, to } = params;

  const queryParams: Record<string, string | number> = {
    page,
    limit,
  };

  if (action && action.trim()) {
    queryParams.action = action.trim();
  }

  if (targetId && targetId.trim()) {
    queryParams.targetId = targetId.trim();
  }

  if (actor && actor.trim()) {
    queryParams.actor = actor.trim();
  }

  if (from && from.trim()) {
    queryParams.from = from.trim();
  }

  if (to && to.trim()) {
    queryParams.to = to.trim();
  }

  const queryString =
    "?" +
    Object.entries(queryParams)
      .map(
        ([key, value]) =>
          `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`
      )
      .join("&");

  const response = await api.get(`${AUDIT_LOGS_ENDPOINT}${queryString}`, undefined);

  if (response && typeof response === "object" && "data" in response && "meta" in response) {
    const data = response as ListAuditLogsResponse;
    return {
      data: Array.isArray(data.data) ? data.data : [],
      meta: data.meta,
    };
  }

  // Fallback for unexpected shapes
  if (Array.isArray(response)) {
    const items = response as AuditLogItem[];
    return {
      data: items,
      meta: {
        total: items.length,
        page: 1,
        limit: items.length,
        pages: 1,
      },
    };
  }

  return {
    data: [],
    meta: {
      total: 0,
      page: 1,
      limit,
      pages: 0,
    },
  };
};

